import { useId, type FormEventHandler, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useI18n } from '../../../i18n/useI18n';
import { LiksModalBackdrop } from '../LiksModalBackdrop/LiksModalBackdrop';
import './LiksDialog.css';

export type LiksDialogSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

type LiksDialogSharedProps = {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  /** Panel width preset. Default `md`. */
  size?: LiksDialogSize;
  /** Optional id for the title heading (auto-generated when omitted). */
  titleId?: string;
  /** Extra controls in the header, left of the corner close button. */
  headerActions?: ReactNode;
  /** Footer actions row. Omit when the dialog has no footer. */
  footer?: ReactNode;
  closeLabel?: string;
  closeDisabled?: boolean;
  /** Blocks Escape, backdrop dismiss, and the close button. */
  isDismissDisabled?: boolean;
  role?: 'dialog' | 'alertdialog';
  'aria-describedby'?: string;
  className?: string;
  bodyClassName?: string;
  footerClassName?: string;
};

export type LiksDialogProps =
  | (LiksDialogSharedProps & {
      as?: 'div';
      onSubmit?: never;
    })
  | (LiksDialogSharedProps & {
      as: 'form';
      onSubmit?: FormEventHandler<HTMLFormElement>;
    });

function noopDismiss() {
  // Intentionally empty — used while dismiss is disabled.
}

export function LiksDialog(props: LiksDialogProps) {
  const {
    isOpen,
    onClose,
    title,
    children,
    size = 'md',
    titleId: titleIdProp,
    headerActions,
    footer,
    closeLabel,
    closeDisabled = false,
    isDismissDisabled = false,
    role = 'dialog',
    'aria-describedby': ariaDescribedBy,
    className,
    bodyClassName,
    footerClassName,
    as = 'div',
  } = props;
  const { translate } = useI18n();
  const generatedTitleId = useId();
  const titleId = titleIdProp ?? generatedTitleId;
  const resolvedCloseLabel = closeLabel ?? translate('common.close');

  if (!isOpen) {
    return null;
  }

  const panelClassName = ['liks-dialog', `liks-dialog--${size}`, className]
    .filter(Boolean)
    .join(' ');

  const closeButton = (
    <button
      type="button"
      className="liks-dialog__close"
      aria-label={resolvedCloseLabel}
      disabled={closeDisabled || isDismissDisabled}
      onClick={onClose}
    >
      <X className="liks-dialog__close-icon" strokeWidth={2} aria-hidden />
    </button>
  );

  const header = (
    <header className="liks-dialog__header">
      <h2 id={titleId} className="liks-dialog__title">
        {title}
      </h2>
      {headerActions != null && (
        <div className="liks-dialog__header-actions">{headerActions}</div>
      )}
    </header>
  );

  const body = (
    <div
      className={['liks-dialog__body', bodyClassName].filter(Boolean).join(' ')}
    >
      {children}
    </div>
  );

  const footerNode =
    footer != null ? (
      <div
        className={['liks-dialog__footer', footerClassName]
          .filter(Boolean)
          .join(' ')}
      >
        {footer}
      </div>
    ) : null;

  const panelProps = {
    className: panelClassName,
    role,
    'aria-modal': true as const,
    'aria-labelledby': titleId,
    'aria-describedby': ariaDescribedBy,
  };

  return (
    <LiksModalBackdrop
      className="liks-dialog__backdrop"
      onDismiss={isDismissDisabled ? noopDismiss : onClose}
    >
      {as === 'form' ? (
        <form {...panelProps} onSubmit={props.onSubmit}>
          {closeButton}
          {header}
          {body}
          {footerNode}
        </form>
      ) : (
        <div {...panelProps}>
          {closeButton}
          {header}
          {body}
          {footerNode}
        </div>
      )}
    </LiksModalBackdrop>
  );
}
